'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { MAX_PENDING_CAPTURES, useDesktopCaptureStore, type PendingCapture } from '../stores/desktopCaptureStore';
import { setPendingCapture } from '../lib/desktopBridge';
import type { PendingAttachmentDraft } from '../types/attachment';

const OPEN_DAEMON_TRIGGER_MODAL_EVENT = 'openDaemonTriggerModal';
const OPEN_FOLLOW_UP_PICKER_EVENT = 'openRunnerRequestFollowUpPicker';
const MAX_THUMBNAILS = 4;

type OpenFollowUpPickerDetail = {
  attachmentDrafts: PendingAttachmentDraft[];
  description: string;
};

/**
 * 우하단 floating chip — 데스크탑 캡처의 webview 진입점.
 *
 * - `useDesktopCaptureStore.captures`가 비어 있지 않을 때만 표시
 * - 모든 활성 캡처(최대 10건)를 한 묶음으로 다룬다 — 1차 액션은 ready 상태인 모든 캡처를 일괄 첨부.
 *   대기/큐 개념 없음.
 * - mode(`new` / `followUp`)는 데스크탑이 보낸 힌트일 뿐, 칩에서는 두 액션을 항상 동시에 노출하고
 *   사용자가 매번 선택한다. 마지막 캡처의 mode가 `followUp`이면 1차 액션을 "이어쓰기"로 추천.
 *
 * 자동 오픈은 절대 하지 않는다 — 사용자가 칩을 클릭해야 모달/picker가 열린다.
 */
export function DesktopCaptureChip(): React.JSX.Element | null {
  const t = useTranslations('components.appLayout');
  const captures = useDesktopCaptureStore((s) => s.captures);
  const dismiss = useDesktopCaptureStore((s) => s.dismiss);
  const dismissAll = useDesktopCaptureStore((s) => s.dismissAll);
  const consumeReady = useDesktopCaptureStore((s) => s.consumeReady);

  const summary = useMemo(() => {
    const total = captures.length;
    const ready = captures.filter((c) => c.status === 'ready').length;
    const uploading = captures.filter((c) => c.status === 'uploading').length;
    const failed = captures.filter((c) => c.status === 'failed').length;
    return { total, ready, uploading, failed };
  }, [captures]);

  const lastMode = captures[captures.length - 1]?.mode ?? 'new';
  const thumbnails = captures.slice(-MAX_THUMBNAILS);

  if (captures.length === 0) return null;

  const dispatchOpen = (target: 'new' | 'followUp'): void => {
    const ready = consumeReady();
    if (ready.length === 0) {
      toast.warning(t('desktopCaptureNoReadyTitle'), {
        description: t('desktopCaptureNoReadyBody'),
      });
      return;
    }
    const drafts = ready.map((c) => c.attachmentDraft).filter((d): d is PendingAttachmentDraft => Boolean(d));

    if (target === 'new') {
      window.dispatchEvent(
        new CustomEvent(OPEN_DAEMON_TRIGGER_MODAL_EVENT, {
          detail: {
            mode: 'create',
            prefillAttachmentDrafts: drafts,
          },
        }),
      );
    } else {
      const description = ready
        .map((c) => c.description)
        .filter(Boolean)
        .join('\n\n');
      window.dispatchEvent(
        new CustomEvent<OpenFollowUpPickerDetail>(OPEN_FOLLOW_UP_PICKER_EVENT, {
          detail: { attachmentDrafts: drafts, description },
        }),
      );
    }
  };

  const onDismissAll = (): void => {
    dismissAll();
    setPendingCapture(null);
    toast.success(t('desktopCaptureDismissAllSuccess'), { duration: 2000 });
  };

  const onDismissOne = (captureId: string): void => {
    dismiss(captureId);
    if (captures.length === 1) setPendingCapture(null);
  };

  const primaryDisabled = summary.ready === 0;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-w-md flex-col items-end gap-2"
      aria-live="polite"
    >
      <div className="pointer-events-auto flex flex-col gap-2 rounded-2xl border border-base-200 bg-base-100 p-3 shadow-lg">
        {/* 헤더: 카운트 + 상태 요약 + 전체 폐기 */}
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-col">
            <div className="text-xs font-semibold text-base-content/80">
              {t('desktopCaptureCountLabel', { total: summary.total })}
              <span className="ml-1 text-[11px] font-normal text-[var(--color-fg-subtle)]">
                {t('desktopCaptureMaxNote', { max: MAX_PENDING_CAPTURES })}
              </span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--color-fg-subtle)]">
              {summary.uploading > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-info" />
                  {t('desktopCaptureUploadingCount', { count: summary.uploading })}
                </span>
              ) : null}
              {summary.ready > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-success" />
                  {t('desktopCaptureReadyCount', { count: summary.ready })}
                </span>
              ) : null}
              {summary.failed > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-error" />
                  {t('desktopCaptureFailedCount', { count: summary.failed })}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="ml-auto rounded-lg border border-transparent p-1.5 text-[var(--color-fg-muted)] transition hover:border-base-200 hover:bg-base-200 hover:text-base-content"
            onClick={onDismissAll}
            aria-label={t('desktopCaptureDismissAll')}
            title={t('desktopCaptureDismissAll')}
          >
            ✕
          </button>
        </div>

        {/* 썸네일 스택 */}
        <div className="flex flex-wrap gap-1">
          {thumbnails.map((c) => (
            <ThumbnailItem
              key={c.captureId}
              capture={c}
              onDismiss={onDismissOne}
              failedLabel={t('desktopCaptureFailedBadge')}
              dismissShortLabel={t('desktopCaptureDismissShort')}
              dismissOneLabel={t('desktopCaptureDismissOne')}
            />
          ))}
          {captures.length > MAX_THUMBNAILS ? (
            <div
              className="flex h-10 w-16 shrink-0 items-center justify-center rounded-lg border border-base-200 bg-base-200 text-[11px] font-semibold text-[var(--color-fg-muted)]"
              aria-label={t('desktopCaptureExtraCount', { count: captures.length - MAX_THUMBNAILS })}
            >
              +{captures.length - MAX_THUMBNAILS}
            </div>
          ) : null}
        </div>

        {/* 액션 — 신규/이어쓰기 둘 다 노출. 마지막 캡처가 followUp이면 이어쓰기가 1차. */}
        <div className="flex items-center justify-end gap-1.5">
          {lastMode === 'followUp' ? (
            <>
              <button
                type="button"
                className="rounded-lg border border-base-200 bg-base-100 px-3 py-1.5 text-xs font-medium text-base-content/80 transition hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => dispatchOpen('new')}
                disabled={primaryDisabled}
              >
                {t('desktopCaptureActionNewRequest')}
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-content transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-base-300 disabled:text-[var(--color-fg-subtle)]"
                onClick={() => dispatchOpen('followUp')}
                disabled={primaryDisabled}
              >
                {t('desktopCaptureActionFollowUpPick')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="rounded-lg border border-base-200 bg-base-100 px-3 py-1.5 text-xs font-medium text-base-content/80 transition hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => dispatchOpen('followUp')}
                disabled={primaryDisabled}
              >
                {t('desktopCaptureActionFollowUp')}
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-content transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-base-300 disabled:text-[var(--color-fg-subtle)]"
                onClick={() => dispatchOpen('new')}
                disabled={primaryDisabled}
              >
                {t('desktopCaptureActionNewRequestCreate')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ThumbnailItem({
  capture,
  onDismiss,
  failedLabel,
  dismissShortLabel,
  dismissOneLabel,
}: {
  capture: PendingCapture;
  onDismiss: (captureId: string) => void;
  failedLabel: string;
  dismissShortLabel: string;
  dismissOneLabel: string;
}): React.JSX.Element {
  return (
    <div className="group relative">
      {capture.thumbnailDataUrl ? (
        /* eslint-disable-next-line @next/next/no-img-element -- desktop capture thumbnails are data URLs. */
        <img
          src={capture.thumbnailDataUrl}
          alt=""
          className="h-10 w-16 shrink-0 rounded-lg border border-base-200 object-cover"
          aria-hidden
        />
      ) : (
        <div className="h-10 w-16 shrink-0 rounded-lg border border-base-200 bg-base-200" aria-hidden />
      )}
      {capture.status === 'uploading' ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-base-100/60">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-info" />
        </div>
      ) : capture.status === 'failed' ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-error/30">
          <span className="text-[10px] font-semibold text-[var(--chip-error-fg)]">{failedLabel}</span>
        </div>
      ) : null}
      <button
        type="button"
        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full border border-base-200 bg-base-100 text-[10px] text-[var(--color-fg-muted)] shadow group-hover:flex hover:bg-base-200 hover:text-base-content"
        onClick={() => onDismiss(capture.captureId)}
        aria-label={dismissOneLabel}
        title={dismissShortLabel}
      >
        ✕
      </button>
    </div>
  );
}
